import type {RouteClaim} from '@wandb/forge-shell-contract';

import type {RemoteName, ShellConfig} from './shellConfig';
import {reservedShellPaths} from './shellPages';
import {isSurface, type Surface} from './surface';

/** A single-segment, slash-prefixed top-level path such as `/notebooks`. */
const topLevelPath = /^\/[a-z0-9][a-z0-9-]*$/i;

export type RemoteRouteClaims = {
  name: RemoteName;
  /** The remote's mount: the shell path its route claim on `/` resolved to. */
  basePath?: string;
  /** Every accepted route claim, longest shell path first. */
  routeClaims: RouteClaim[];
};

export type RouteClaimTable = readonly RemoteRouteClaims[];

export type ActiveRouteClaim = {
  remote: RemoteRouteClaims;
  routeClaim: RouteClaim;
};

function trimTrailingSlash(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

function isUnder(pathname: string, prefix: string): boolean {
  if (prefix === '/') {
    return pathname.startsWith('/');
  }
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

const ACCOUNT_SEGMENT = '/:account';

/** Exact match, or subtree match for entries ending in `/*`. */
export function preservesOnAccountSwitch(
  routeClaim: RouteClaim,
  remotePath: string
): boolean {
  const path = trimTrailingSlash(remotePath);
  const list = routeClaim.preserveOnAccountSwitch ?? [routeClaim.remotePath];
  return list.some(entry =>
    entry.endsWith('/*')
      ? path.startsWith(entry.slice(0, -1))
      : path === trimTrailingSlash(entry)
  );
}

/** The `/:account` entry covering `remotePath`, filled in for the new account. */
export function rewriteOnAccountSwitch(
  routeClaim: RouteClaim,
  remotePath: string,
  accountName: string
): string | undefined {
  const path = trimTrailingSlash(remotePath);
  for (const entry of routeClaim.preserveOnAccountSwitch ?? []) {
    if (!entry.endsWith(ACCOUNT_SEGMENT)) {
      continue;
    }
    const prefix = entry.slice(0, -ACCOUNT_SEGMENT.length) || '/';
    if (isUnder(path, prefix)) {
      return joinPrefix(prefix, `/${encodeURIComponent(accountName)}`);
    }
  }
  return undefined;
}

function stripPrefix(pathname: string, prefix: string): string {
  if (prefix === '/') {
    return pathname;
  }
  return pathname.slice(prefix.length) || '/';
}

function joinPrefix(prefix: string, rest: string): string {
  if (prefix === '/') {
    return rest;
  }
  return rest === '/' ? prefix : `${prefix}${rest}`;
}

/**
 * Resolves the route claims remotes published in their manifests, for one surface.
 * Every path a remote is served at is a route claim, mount included, so a remote the
 * shell has no manifest for is simply not on the surface.
 */
export function resolveRouteClaims(
  remotes: ShellConfig['remotes'],
  surface: Surface = 'forge'
): RouteClaimTable {
  // Runtime config has one entry per configured remote, so the shell has no
  // second list of remote names to keep in step with server configuration.
  const names = Object.keys(remotes ?? {}) as RemoteName[];
  const table: RemoteRouteClaims[] = names.map(name => ({
    name,
    routeClaims: [],
  }));

  // Deploy order must not decide which product answers a URL, so contested
  // paths are collected first and every route claim on them is then dropped.
  const routeClaimants = new Map<
    string,
    Array<{name: RemoteName; routeClaim: RouteClaim}>
  >();
  for (const remote of table) {
    for (const route of remotes?.[remote.name]?.routes ?? []) {
      const {shellPath, remotePath, preserveOnAccountSwitch} = route;
      if (!isSurface(route.surface)) {
        console.error(
          `Forge shell: ${remote.name} claims "${shellPath}" on unknown surface "${route.surface}".`
        );
        continue;
      }
      if (route.surface !== surface) {
        continue;
      }
      if (!topLevelPath.test(shellPath)) {
        console.error(
          `Forge shell: ${remote.name} claims "${shellPath}", which is not a single-segment top-level path.`
        );
        continue;
      }
      if (typeof remotePath !== 'string' || !remotePath.startsWith('/')) {
        console.error(
          `Forge shell: ${remote.name} claims "${shellPath}" for remote path "${remotePath}", which is not absolute.`
        );
        continue;
      }
      if (reservedShellPaths.has(shellPath)) {
        console.error(
          `Forge shell: ${remote.name} claims "${shellPath}", which the shell owns.`
        );
        continue;
      }
      const routeClaims = routeClaimants.get(shellPath) ?? [];
      routeClaims.push({
        name: remote.name,
        routeClaim: {
          surface,
          shellPath,
          remotePath,
          ...(preserveOnAccountSwitch !== undefined
            ? {preserveOnAccountSwitch}
            : {}),
        },
      });
      routeClaimants.set(shellPath, routeClaims);
    }
  }

  const byName = new Map(table.map(remote => [remote.name, remote]));
  for (const [shellPath, routeClaims] of routeClaimants) {
    if (routeClaims.length > 1) {
      console.error(
        `Forge shell: "${shellPath}" is claimed by ${routeClaims
          .map(entry => entry.name)
          .join(', ')}; dropping every routeClaim on it.`
      );
      continue;
    }
    const [{name, routeClaim}] = routeClaims;
    byName.get(name)?.routeClaims.push(routeClaim);
  }

  for (const remote of table) {
    remote.routeClaims.sort((a, b) => b.shellPath.length - a.shellPath.length);
    remote.basePath = remote.routeClaims.find(
      routeClaim => routeClaim.remotePath === '/'
    )?.shellPath;
  }
  return table;
}

let cache:
  | {remotes: unknown; surface: Surface; table: RouteClaimTable}
  | undefined;

/** The resolved table for the current shell config, resolved once per config. */
export function shellRouteClaimTable(
  surface: Surface = 'forge',
  remotes = window.__FORGE_SHELL_CONFIG__?.remotes
): RouteClaimTable {
  if (!cache || cache.remotes !== remotes || cache.surface !== surface) {
    cache = {remotes, surface, table: resolveRouteClaims(remotes, surface)};
  }
  return cache.table;
}

/** The remote that renders a shell path, by longest claimed shell path. */
export function activeRouteClaim(
  table: RouteClaimTable,
  pathname: string
): ActiveRouteClaim | undefined {
  let active: ActiveRouteClaim | undefined;
  for (const remote of table) {
    for (const routeClaim of remote.routeClaims) {
      if (!isUnder(pathname, routeClaim.shellPath)) {
        continue;
      }
      if (
        !active ||
        routeClaim.shellPath.length > active.routeClaim.shellPath.length
      ) {
        active = {remote, routeClaim};
      }
    }
  }
  return active;
}

/** The remote holding a route claim on exactly this shell path. */
export function routeClaimOwner(
  table: RouteClaimTable,
  shellPath: string
): RemoteRouteClaims | undefined {
  return table.find(remote =>
    remote.routeClaims.some(routeClaim => routeClaim.shellPath === shellPath)
  );
}

/** Translates a shell path into the claimed remote's own path. */
export function remotePathname(
  routeClaim: RouteClaim,
  pathname: string
): string {
  if (!isUnder(pathname, routeClaim.shellPath)) {
    throw new Error(
      `Cannot translate "${pathname}" with route claim "${routeClaim.shellPath}".`
    );
  }
  return joinPrefix(
    routeClaim.remotePath,
    stripPrefix(pathname, routeClaim.shellPath)
  );
}

/**
 * Translates a remote path into its canonical shell path, by longest claimed
 * remote path. A claimed remote path leaves the base route claim, so `/notebooks`
 * resolves to `/notebooks` rather than `/wandb/notebooks`.
 */
export function shellPathname(
  routeClaims: readonly RouteClaim[],
  remotePath: string
): string {
  let best: RouteClaim | undefined;
  for (const routeClaim of routeClaims) {
    if (!isUnder(remotePath, routeClaim.remotePath)) {
      continue;
    }
    if (!best || routeClaim.remotePath.length > best.remotePath.length) {
      best = routeClaim;
    }
  }
  if (!best) {
    return remotePath;
  }
  return joinPrefix(best.shellPath, stripPrefix(remotePath, best.remotePath));
}

/** Translates a path for one remote when that remote claims the path. */
export function shellPathForRemotePath(
  table: RouteClaimTable,
  remoteName: RemoteName,
  remotePath: string
): string | undefined {
  const remote = table.find(candidate => candidate.name === remoteName);
  if (remote == null) {
    return undefined;
  }
  // A remote path may carry a query or hash (`/settings#apikeys`); only the
  // pathname takes part in matching a route claim.
  const suffixStart = remotePath.search(/[?#]/);
  const pathname =
    suffixStart === -1 ? remotePath : remotePath.slice(0, suffixStart);
  const suffix = suffixStart === -1 ? '' : remotePath.slice(suffixStart);
  if (!remote.routeClaims.some(route => isUnder(pathname, route.remotePath))) {
    return undefined;
  }
  return shellPathname(remote.routeClaims, pathname) + suffix;
}

/**
 * Where a catalog entry points: its remote's mount, or — for entries with no
 * remote of their own, such as Notebooks — the remote that claimed `/${id}`.
 */
export function catalogHref(
  entry: {id: string; path?: string; remote?: RemoteName},
  table: RouteClaimTable
): string | undefined {
  if (entry.remote) {
    return table.find(candidate => candidate.name === entry.remote)?.basePath;
  }
  const shellPath = entry.path ?? `/${entry.id}`;
  return routeClaimOwner(table, shellPath) ? shellPath : undefined;
}
